// B-89 — "Enviar correo de oficina(s)", from the panel's selection bar: each selected closing's
// office gets ITS OWN email — its figures, open alerts, the team's observations, missing reports and
// its row of the Matriz — at ITS OWN addresses (kept in Oficinas). Not the client's consolidated
// report: that is "Enviar consolidado".
//
// Before sending, the dialog lists every office with the addresses it will go to, and says which
// have none (they are skipped, not a reason to stop the others). "Vista previa" shows each office's
// email exactly as it will arrive (`previewCierreOfficeReport`: built, never sent). While sending,
// the backend answers office by office and the dialog says which one it is on; at the end it lists
// each office with how it went.
//
// The send needs `cierres.export` on every office in it; the caller passes the offices this person
// may not send (`sinPermiso`) and the dialog refuses before asking the server.
//
// An office's addresses can be added or changed right here ("Agregar correos" / "Editar"), saved to
// the office itself (`saveCierreOfficeContacts`, the same save the Oficinas screen makes) — offered
// only where the server says `editable` (`cierres.offices.contacts` on that office).
import { useEffect, useState } from "react";
import { ArrowLeft, Eye, Mail, Pencil, Plus } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import {
  listCierreOfficeContacts,
  previewCierreOfficeReport,
  saveCierreOfficeContacts,
  sendCierreOfficeReport,
} from "../../../ApiCalls/mantisBusiness";
import { correosEscritos } from "../../../utils/cierreConsolidado";
import { ESTADO_ENVIO, resultadoDeEnvioOficinas } from "../../../utils/cierreCorreosOficina";
import { correctionError } from "../../../utils/cierreCorrections";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const TONO_ESTADO = {
  SENT: "text-brinks-success",
  PREVIEW: "text-brinks-foreground",
  NO_RECIPIENTS: "text-brinks-warning",
  FAILED: "text-brinks-danger",
};

export function EnviarCorreoOficinas({
  open,
  onOpenChange,
  token,
  mantisBusinessId,
  clientCode,
  filas = [],
  sinPermiso = [],
}) {
  const [correos, setCorreos] = useState(null);
  const [errorCorreos, setErrorCorreos] = useState(null);
  const [comentario, setComentario] = useState("");
  const [vista, setVista] = useState(null);
  const [vistaActual, setVistaActual] = useState(0);
  const [previsualizando, setPrevisualizando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [resultado, setResultado] = useState(null);
  // The offices whose addresses this person may change here, and the one being edited.
  const [editables, setEditables] = useState(new Set());
  const [editando, setEditando] = useState(null);
  const [textoCorreos, setTextoCorreos] = useState("");
  const [guardandoCorreos, setGuardandoCorreos] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState(null);

  // Each office's addresses, asked when the dialog opens — they may have been changed in Oficinas.
  useEffect(() => {
    if (!open) return undefined;
    let activo = true;
    setCorreos(null);
    setErrorCorreos(null);
    setVista(null);
    setResultado(null);
    setProgreso(null);
    (async () => {
      const response = await listCierreOfficeContacts(token, mantisBusinessId, clientCode).catch(() => null);
      if (!activo) return;
      if (response?.success) {
        const oficinas = response.data.offices || [];
        setCorreos(new Map(oficinas.map((o) => [String(o.officeCode), o.emails || []])));
        setEditables(new Set(oficinas.filter((o) => o.editable).map((o) => String(o.officeCode))));
      } else {
        setErrorCorreos(response?.errorCode || response?.message ? correctionError(response) : "No se pudieron leer los correos de las oficinas.");
        setCorreos(new Map());
      }
    })();
    return () => {
      activo = false;
    };
  }, [open, token, mantisBusinessId, clientCode]);

  const ids = filas.map((f) => f.closingId).filter(Boolean);
  const sinCorreos = correos ? filas.filter((f) => !(correos.get(String(f.codigo)) || []).length) : [];
  const conCorreos = filas.length - sinCorreos.length;
  const bloqueado = sinPermiso.length > 0;
  const ocupado = enviando || previsualizando || editando !== null;
  const plural = filas.length === 1 ? "oficina" : "oficinas";

  const escritos = correosEscritos(textoCorreos);

  const editar = (codigo) => {
    setEditando(codigo);
    setTextoCorreos((correos.get(codigo) || []).join(", "));
    setErrorEdicion(null);
  };

  // Saved to the office, not only to this send: the next report finds them there too.
  const guardarCorreos = async () => {
    setGuardandoCorreos(true);
    setErrorEdicion(null);
    const response = await saveCierreOfficeContacts(token, mantisBusinessId, clientCode, [
      { officeCode: editando, emails: escritos.correos },
    ]).catch(() => null);
    setGuardandoCorreos(false);
    if (response?.success) {
      setCorreos((actuales) => new Map(actuales).set(editando, escritos.correos));
      setEditando(null);
    } else {
      setErrorEdicion(response?.errorCode || response?.message ? correctionError(response) : "No se pudieron guardar los correos.");
    }
  };

  const previsualizar = async () => {
    setPrevisualizando(true);
    setResultado(null);
    const response = await previewCierreOfficeReport(token, mantisBusinessId, ids, comentario).catch(() => null);
    setPrevisualizando(false);
    if (response?.success) {
      setVista(response.data.offices || []);
      setVistaActual(0);
    } else {
      setResultado(resultadoDeEnvioOficinas(response));
    }
  };

  const enviar = async () => {
    setEnviando(true);
    setResultado(null);
    setProgreso(null);
    const response = await sendCierreOfficeReport(
      token,
      mantisBusinessId,
      ids,
      (evento) => {
        if (evento.step === "office" && evento.state === "running") setProgreso(evento.detail);
      },
      comentario
    ).catch(() => null);
    setResultado(resultadoDeEnvioOficinas(response));
    setEnviando(false);
    setProgreso(null);
  };

  const oficinaVista = vista ? vista[vistaActual] : null;
  const terminado = resultado && resultado.oficinas.length > 0;

  return (
    <Dialog open={open} onOpenChange={(abierto) => !enviando && onOpenChange(abierto)}>
      <DialogContent className={cn("brinks-scope font-redhat", vista ? "max-w-3xl" : "max-w-lg")}>
        <DialogHeader className="text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
              {filas.length === 1 ? "Enviar el cuadre a la oficina" : `Enviar el cuadre a ${filas.length} oficinas`}
            </DialogTitle>
            <BotonGuia guiaId="dialogo-enviar-oficinas" titulo="Enviar a las oficinas" texto="Cada oficina elegida recibe su propio correo, en sus propias direcciones: sus cifras, sus alertas abiertas y lo que le falta." nombre="Enviar a las oficinas" />
          </div>
          <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
            Cada oficina recibe su propio correo, a sus propios correos: sus cifras, sus alertas, las
            observaciones del equipo y su fila de la Matriz en Excel.
          </DialogDescription>
        </DialogHeader>

        {vista ? (
          <div className="grid gap-2">
            {vista.length > 1 ? (
              <div className="flex flex-wrap gap-1" role="tablist" aria-label="Oficinas">
                {vista.map((o, i) => (
                  <button
                    key={o.closingId}
                    type="button"
                    role="tab"
                    aria-selected={i === vistaActual}
                    onClick={() => setVistaActual(i)}
                    className={cn(
                      "h-7 rounded-[6px] border px-2 text-[11.5px] font-medium",
                      i === vistaActual ? "bg-primary text-primary-foreground" : "bg-card text-brinks-foreground hover:bg-brinks-surface-hover"
                    )}
                  >
                    {o.officeCode}
                  </button>
                ))}
              </div>
            ) : null}
            {oficinaVista ? (
              <>
                <p className="truncate text-[12.5px] text-brinks-foreground" title={oficinaVista.subject}>
                  <span className="text-brinks-muted-foreground">Asunto: </span>
                  {oficinaVista.subject}
                </p>
                <p className="truncate text-[12px] text-brinks-muted-foreground">
                  Para:{" "}
                  {oficinaVista.recipients?.length ? (
                    oficinaVista.recipients.join(", ")
                  ) : (
                    <span className="text-brinks-warning">sin correos — esta oficina no recibirá el correo</span>
                  )}
                </p>
                {/* The email as it will arrive. `sandbox` with nothing allowed: an email body has no
                    business running anything here. */}
                <iframe
                  {...guia("Vista previa", "El correo tal como va a llegar. \"Volver a editar\" conserva lo que escribiste.")}
                  title="Vista previa del correo"
                  srcDoc={oficinaVista.html}
                  sandbox=""
                  className="h-[55vh] w-full rounded-[8px] border bg-white"
                />
              </>
            ) : null}
          </div>
        ) : terminado ? (
          <ul {...guia("Resultado", "Qué pasó con cada oficina: enviado, sin correos o no se pudo enviar.")} aria-label="Resultado del envío" className="grid max-h-[45vh] gap-1 overflow-y-auto rounded-[8px] border bg-brinks-surface p-2">
            {resultado.oficinas.map((o) => (
              <li key={o.closingId} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                <span className="min-w-0 truncate">
                  <span className="font-medium">{o.officeName}</span>{" "}
                  <span className="text-brinks-muted-foreground">({o.officeCode})</span>
                </span>
                <span className={cn("shrink-0 font-medium", TONO_ESTADO[o.status])}>{ESTADO_ENVIO[o.status] || o.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <>
            {correos === null ? (
              <div className="grid place-items-center py-6">
                <Spinner size="lg" />
              </div>
            ) : (
              <ul {...guia("Oficinas", "Cada oficina con los correos a los que va. Una oficina sin correos se salta; \"Agregar correos\" o \"Editar\" los guarda en la oficina.")} aria-label="Oficinas a las que se envía" className="grid max-h-[40vh] gap-1.5 overflow-y-auto rounded-[8px] border bg-brinks-surface p-2">
                {filas.map((f) => {
                  const codigo = String(f.codigo);
                  const lista = correos.get(codigo) || [];
                  const puedeEditar = editables.has(codigo);
                  if (editando === codigo) {
                    return (
                      <li key={f.id} className="grid gap-1 rounded-[6px] border bg-card p-2 text-[12.5px]">
                        <label htmlFor={`correos-${codigo}`} className="min-w-0 truncate">
                          <span className="font-medium">{f.oficina}</span>{" "}
                          <span className="text-brinks-muted-foreground">({f.codigo})</span>
                        </label>
                        <input
                          id={`correos-${codigo}`}
                          autoFocus
                          value={textoCorreos}
                          disabled={guardandoCorreos}
                          onChange={(e) => setTextoCorreos(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !escritos.invalidos.length) guardarCorreos();
                            if (e.key === "Escape") setEditando(null);
                          }}
                          placeholder="oficina@empresa.com, jefe@empresa.com"
                          className="h-8 w-full rounded-[6px] border bg-card px-2 text-[12.5px] outline-none focus:border-brinks-border-strong"
                        />
                        <span className={cn("text-[11px]", escritos.invalidos.length || errorEdicion ? "text-brinks-danger" : "text-brinks-muted-foreground")}>
                          {errorEdicion ||
                            (escritos.invalidos.length
                              ? `No es un correo válido: ${escritos.invalidos.join(", ")}`
                              : "Separados por comas. Quedan guardados en la oficina.")}
                        </span>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-[12px]" disabled={guardandoCorreos} onClick={() => setEditando(null)}>
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 gap-1 text-[12px]"
                            disabled={guardandoCorreos || escritos.invalidos.length > 0}
                            onClick={guardarCorreos}
                          >
                            {guardandoCorreos ? <Spinner size="sm" /> : null} Guardar
                          </Button>
                        </div>
                      </li>
                    );
                  }
                  return (
                    <li key={f.id} className="flex items-start justify-between gap-2 text-[12.5px]">
                      <span className="grid min-w-0 gap-0.5">
                        <span className="min-w-0 truncate">
                          <span className="font-medium">{f.oficina}</span>{" "}
                          <span className="text-brinks-muted-foreground">({f.codigo})</span>
                        </span>
                        <span className={cn("truncate text-[11.5px]", lista.length ? "text-brinks-muted-foreground" : "text-brinks-warning")}>
                          {lista.length
                            ? lista.join(", ")
                            : puedeEditar
                              ? "Sin correos: no se le enviará."
                              : "Sin correos: no se le enviará. No tienes permiso para agregarlos."}
                        </span>
                      </span>
                      {puedeEditar ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 shrink-0 gap-1 px-2 text-[12px]"
                          disabled={enviando || previsualizando || editando !== null}
                          onClick={() => editar(codigo)}
                        >
                          {lista.length ? <Pencil className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                          {lista.length ? "Editar" : "Agregar correos"}
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {errorCorreos ? <p className="text-[11.5px] text-brinks-danger">{errorCorreos}</p> : null}
            {bloqueado ? (
              <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12px] text-brinks-danger">
                Tu perfil no permite enviar el cuadre de {sinPermiso.length === 1 ? "la oficina" : "las oficinas"}{" "}
                {sinPermiso.join(", ")}. Quítalas de la selección para enviar a las demás.
              </p>
            ) : null}
            <div className="grid gap-1">
              <label htmlFor="enviar-oficinas-comentario" className="text-[12px] font-medium text-brinks-muted-foreground">
                Comentario (opcional)
              </label>
              <textarea
                {...guia("Comentario", "Opcional: va al inicio de cada correo, firmado con tu nombre.")}
                id="enviar-oficinas-comentario"
                rows={3}
                maxLength={2000}
                value={comentario}
                disabled={ocupado}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Lo que quieras que la oficina lea primero."
                className="min-h-[72px] w-full resize-y rounded-[8px] border bg-card px-2.5 py-1.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong"
              />
              <span className="text-[11.5px] text-brinks-muted-foreground">Va al inicio de cada correo, con tu nombre.</span>
            </div>
          </>
        )}

        {enviando ? (
          <p role="status" className="flex items-center gap-2 text-[12.5px] text-brinks-foreground">
            <Spinner size="sm" />
            {progreso ? `Enviando ${progreso.index} de ${progreso.total} · oficina ${progreso.officeCode}` : "Preparando los correos…"}
          </p>
        ) : null}

        {resultado ? (
          <p
            role={resultado.ok ? "status" : "alert"}
            className={cn(
              "rounded-[8px] border px-2.5 py-1.5 text-[12.5px]",
              resultado.ok
                ? "border-brinks-success-border bg-brinks-success-soft text-brinks-success"
                : "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning"
            )}
          >
            {resultado.texto}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          {terminado ? (
            <Button className="h-9" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          ) : (
            <>
              {vista ? (
                <Button variant="outline" className="h-9 gap-1.5" disabled={enviando} onClick={() => setVista(null)}>
                  <ArrowLeft className="h-4 w-4" /> Volver a editar
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="h-9" disabled={ocupado} onClick={() => onOpenChange(false)}>
                    Cancelar
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9 gap-1.5"
                    disabled={ocupado || bloqueado || correos === null}
                    onClick={previsualizar}
                  >
                    {previsualizando ? <Spinner size="sm" /> : <Eye className="h-4 w-4" />} Vista previa
                  </Button>
                </>
              )}
              <Button
                className="h-9 gap-1.5"
                {...guia("Enviar", "Envía un correo a cada oficina con correos. Mientras envía te dice en qué oficina va.")}
                disabled={ocupado || bloqueado || correos === null || conCorreos === 0}
                title={conCorreos === 0 && correos ? "Ninguna oficina seleccionada tiene correos" : undefined}
                onClick={enviar}
              >
                {enviando ? <Spinner size="sm" /> : <Mail className="h-4 w-4" />}{" "}
                {enviando ? "Enviando…" : `Enviar a ${conCorreos} ${conCorreos === 1 ? "oficina" : "oficinas"}`}
              </Button>
            </>
          )}
        </DialogFooter>
        {!vista && !terminado && sinCorreos.length > 0 && conCorreos > 0 ? (
          <p className="-mt-1 text-right text-[11px] text-brinks-muted-foreground">
            {sinCorreos.length} de {filas.length} {plural} sin correos no se incluyen.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default EnviarCorreoOficinas;
